import { colors } from 'quasar'
import { defineStore } from 'pinia'
import { computed, ref, toRaw } from 'vue'
import { updateLocaleAsync, getTranslatedItems, getI18n, getLocalizedEnumItems } from '@/plugins/i18n'

import { useFetchAndStoreAsync } from '@/composables/store'

import { portalApi } from '@/apis/portal.api'

import { updateQuasarLocaleAsync } from '@/plugins/quasar'
import { textColor } from '@/lib/utils'

export const initPortalStoreAsync = async() => {}

// Mirrors backend accounting.Weekday (ISO numbering, as dayjs isoWeekday).
export const weekdayEnum = Object.freeze({
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
})

// Mirrors backend web_site.PortalAccessStatus.
export const portalAccessStatusEnum = Object.freeze({
  NO_ACCESS: 0,
  PENDING: 1,
  ACTIVATED: 2,
  EXPIRED: 3,
})

// Mirrors backend lib.enums.ExchangeRateSource.
export const exchangeRateSourceEnum = Object.freeze({
  MANUAL: 0,
  ECB: 1,
  ECB_DERIVED: 2,
})

export const serviceTypeEnum = Object.freeze({
  STAFF: 0,
  EQUIPMENT: 1,
  GROUP: 2,
})

export const serviceTypeIcons = Object.freeze({
  [serviceTypeEnum.STAFF]: 'mdi-account',
  [serviceTypeEnum.EQUIPMENT]: 'mdi-camera-outline',
  [serviceTypeEnum.GROUP]: 'mdi-select-group',
})

// Mirrors backend lib.enums.ExtraFieldType.
export const extraFieldTypeEnum = Object.freeze({
  TEXT: 0,
  NUMBER: 1,
  SELECT: 2,
  DATE: 3,
  BOOLEAN: 4,
})

const extraFieldTypeIcons = Object.freeze({
  [extraFieldTypeEnum.TEXT]: 'mdi-form-textbox',
  [extraFieldTypeEnum.NUMBER]: 'mdi-numeric',
  [extraFieldTypeEnum.SELECT]: 'mdi-form-dropdown',
  [extraFieldTypeEnum.DATE]: 'mdi-calendar-outline',
  [extraFieldTypeEnum.BOOLEAN]: 'mdi-toggle-switch-outline',
})

export const serviceAvailabilityEnum = Object.freeze({
  UNAVAILABLE: 0,
  PARTIALLY_AVAILABLE: 1,
  AVAILABLE: 2,
})

export const serviceAvailabilityTypeEnum = Object.freeze({
  USUALLY_UNAVAILABLE: 0,
  USUALLY_AVAILABLE: 1,
})

export const serviceStructureTypeEnum = Object.freeze({
  PHYSICAL: 1,
  GROUP_PHYSICAL: 2,
  GROUP_VIRTUAL: 3,
})

export const drivingLicenseTypeEnum = Object.freeze({
  AM: 1,
  A1: 2,
  A2: 3,
  A: 4,
  B1: 5,
  B: 6,
  BE: 7,
  C1: 8,
  C1E: 9,
  C: 10,
  CE: 11,
  D1: 12,
  D1E: 13,
  D: 14,
  DE: 15,
})

export const notificationTypeEnum = Object.freeze({
  INFO: 0,
  ASSIGNMENT_CHANGED: 1,
  ASSIGNMENT_ADDED: 2,
  ASSIGNMENT_REMOVED: 3,
  ALLOCATION_CONFIRMED: 4,
  ABSENCE_APPROVED: 5,
  ABSENCE_REVERTED: 6,
  PROJECT_DATES_CHANGED: 7,
  ABSENCE_REQUESTED: 8,
  STAFF_ABSENCE_REVERTED: 9,
  MOVEMENTS_TO_CONFIRM: 10,
  MOVEMENTS_CONFIRMED: 11,
  INVOICES_DUE: 12,
  INVOICES_OVERDUE: 13,
  QUOTE_CREATED: 14,
  PROJECT_COMPLETED_NOT_INVOICED: 15,
  INVOICE_SENT: 17,
  EQUIPMENT_BROKEN: 18,
  RESOURCE_SHORTAGE: 19,
  STOCK_BELOW_MINIMUM: 20,
  TASK_ASSIGNED: 21,
  TASK_OVERDUE: 22,
})

export const notificationCategoryEnum = Object.freeze({
  MY_WORK: 0,
  OPERATIONS: 1,
  ACCOUNTING: 2,
  SYSTEM: 3,
})

export const notificationScopeEnum = Object.freeze({
  INVOLVED: 0,
  ALL: 1,
})

export const notificationTargetEnum = Object.freeze({
  NONE: 0,
  TIMELINE: 1,
  PROJECT: 2,
  QUOTE: 3,
  INVOICES: 4,
  WAREHOUSE_MOVEMENTS: 5,
  STAFF_ABSENCES: 6,
  WAREHOUSE_AVAILABILITY: 7,
  EQUIPMENT: 8,
  TASKS: 9,
})

export const notificationFrequencyEnum = Object.freeze({
  EVERY_15_MINUTES: 0,
  EVERY_30_MINUTES: 1,
  EVERY_HOUR: 2,
  EVERY_2_HOURS: 3,
  EVERY_4_HOURS: 4,
  EVERY_8_HOURS: 5,
  DAILY: 6,
})

export const notificationReminderEnum = Object.freeze({
  NEVER: 0,
  DAILY: 1,
  EVERY_3_DAYS: 2,
  WEEKLY: 3,
})

const notificationCategoryPaletteEnum = Object.freeze({
  [notificationCategoryEnum.MY_WORK]: 'primary',
  [notificationCategoryEnum.OPERATIONS]: 'teal',
  [notificationCategoryEnum.ACCOUNTING]: 'blue',
  [notificationCategoryEnum.SYSTEM]: 'grey-7',
})

export const notificationAttentionTypes = Object.freeze([
  notificationTypeEnum.ABSENCE_REVERTED,
  notificationTypeEnum.MOVEMENTS_TO_CONFIRM,
  notificationTypeEnum.INVOICES_OVERDUE,
  notificationTypeEnum.PROJECT_COMPLETED_NOT_INVOICED,
  notificationTypeEnum.EQUIPMENT_BROKEN,
  notificationTypeEnum.RESOURCE_SHORTAGE,
  notificationTypeEnum.TASK_OVERDUE,
])

// The level of a permission feature in the matrix, derived from its view and full flags.
export const accessLevelEnum = Object.freeze({
  NONE: 0,
  VIEW: 1,
  FULL: 2,
})

// Mirrors backend web_site.PermissionFlag: the bits of the `permissions` JWT claim.
export const permissionFlagEnum = Object.freeze({
  ALL_PROJECTS: 1 << 0,
  PROJECTS: 1 << 1,
  MASTER_DATA: 1 << 2,
  PLANNING: 1 << 3,
  AI_ASSISTANT: 1 << 4,
  RATE_CARDS_VIEW: 1 << 5,
  RATE_CARDS_FULL: 1 << 6,
  QUOTES_VIEW: 1 << 7,
  QUOTES_FULL: 1 << 8,
  INVOICING_VIEW: 1 << 9,
  INVOICING_FULL: 1 << 10,
  FINANCIALS: 1 << 11,
  EXPORT: 1 << 12,
  REPORTS: 1 << 13,
  APPROVALS: 1 << 14,
  REIMBURSEMENTS: 1 << 15,
  COMPANY_SETTINGS: 1 << 16,
  USERS_PERMISSIONS: 1 << 17,
  DELETE: 1 << 18,
})

// Mirrors backend web_site.SystemRoleEnum.
export const systemRoleEnum = Object.freeze({
  ADMINISTRATOR: 0,
  USER: 1,
  AI_ASSISTANT: 2,
})

// Mirrors backend accounting.TimelinePeriod (CUSTOM is frontend-only).
export const dateRangePresetEnum = Object.freeze({
  WEEK: 0,
  TWO_WEEKS: 1,
  MONTH: 2,
  QUARTER: 3,
  SEMESTER: 4,
  YEAR: 5,
  CUSTOM: 6,
  THIRTY_DAYS: 7,
})

// Generic duration options (values in days), mirrors backend accounting.DurationDays.
// Reused wherever a duration threshold is picked (e.g. CompanyData.repair_on_time_days).
export const durationDaysOptions = Object.freeze([
  { value: 1, labelKey: 'common.duration.oneDay' },
  { value: 2, labelKey: 'common.duration.twoDays' },
  { value: 3, labelKey: 'common.duration.threeDays' },
  { value: 4, labelKey: 'common.duration.fourDays' },
  { value: 7, labelKey: 'common.duration.oneWeek' },
  { value: 14, labelKey: 'common.duration.twoWeeks' },
  { value: 21, labelKey: 'common.duration.threeWeeks' },
  { value: 30, labelKey: 'common.duration.oneMonth' },
  { value: 60, labelKey: 'common.duration.twoMonths' },
  { value: 90, labelKey: 'common.duration.threeMonths' },
])

export const infoBoxToneEnum = Object.freeze({
  INFO: 0,
  SUCCESS: 1,
  WARNING: 2,
  QUESTION: 3,
})

export const infoBoxIdEnum = Object.freeze({
  PERMISSIONS_NOTE: 0,
  INVOICE_NUMBER_FORMAT: 1,
  INVOICE_LINES_EDITABLE: 2,
  SERVICE_IMPORT_FILE_HINT: 3,
  SERVICE_IMPORT_LOCATION_HINT: 4,
  MOVE_ALLOCATIONS: 5,
  SERVICE_GROUP_INFO: 6,
  RATE_CARD_GENERIC_LISTING: 7,
  MANAGE_STOCK_FROM_WAREHOUSE: 8,
  ROLE_PERMISSIONS_HINT: 9,
  VIRTUAL_LOCATION: 10,
  TRANSIT_ORDERS_VIRTUAL: 11,
  AVAILABILITY_PIVOT: 12,
  ECB_DISCLAIMER: 13,
  PORTAL_ACCESS_ROLE_HINT: 14,
  REPLACE_SERVICE: 15,
  REPORT_PROJECT_INCLUSION: 16,
  REPORT_ROWS_EDITABLE: 17,
  REPORT_MY_SUMMARIES: 18,
  NIGHT_WINDOW: 19,
  REPORT_COLUMNS: 20,
  MY_EXPENSES_HINT: 22,
  EXPENSE_ITEMS: 23,
  EXPENSE_CLAIM_SETTINGS: 25,
  REPORT_CONVENTION_SCOPE: 28,
  REPORT_LAYOUT_AT_PRINT: 40,
  EXPENSE_CLAIM_ROWS: 42,
  EXPENSE_CLAIM_SUMMARY: 43,
  REPORT_DOCUMENT_LANGUAGE: 44,
  EXPENSE_CLAIM_APPROVAL: 46,
  REPORT_COLUMN_TEMPLATE_DEFAULTS: 47,
  REPORT_COLUMN_TEMPLATES: 48,
  TASK_TYPES: 50,
  NOTIFICATION_SETTINGS: 51,
})

// Mirrors backend lib.enums.WarningEnum (string label keys). Used to tell the "in use by
// projects, archive instead?" suggestion apart from the "still allocated now, replace it
// first" hard block on the delete flow.
export const warningCodeEnum = Object.freeze({
  ELEMENT_IN_USE_BY_PROJECTS: 'warning.elementInUseByProjects',
  SERVICE_ARCHIVE_IN_USE: 'warning.serviceArchiveInUse',
  WAREHOUSE_IS_DEFAULT: 'warning.warehouseIsDefault',
  SERVICE_NOT_IN_PROJECT_LISTING: 'warning.serviceNotInProjectListing',
})

export const entityChangeTypeEnum = Object.freeze({
  STAFF: 0,
  EQUIPMENT: 1,
  GROUP: 2,
  COMPANY: 3,
  PROJECT: 4,
  QUOTE: 5,
  COST_REPORT_ROW: 6,
  EVENT: 7,
  TASK: 8,
  LISTING: 9,
})

export const usePortalStore = defineStore('portal', () => {
  const api = portalApi
  const reloadKey = ref(0)
  const fontSize = ref(1)
  const languages = ref([])
  const localizations = ref({})
  const selectedLanguage = ref(window.config.DEFAULT_LANGUAGE)
  const tags = ref([])
  const version = ref(null)

  const incrementReloadKey = () => { reloadKey.value++ }

  const lastEntityChange = ref(null)
  const entityChangeSubscribers = ref({})
  const notifyEntityChange = (types, { id = null, created = false } = {}) => { lastEntityChange.value = { types, id, created } }
  const subscribeEntityChanges = (types, step = 1) => types.forEach(type => {
    entityChangeSubscribers.value[type] = (entityChangeSubscribers.value[type] ?? 0) + step
  })
  const hasEntityChangeSubscribers = (types) => types.some(type => entityChangeSubscribers.value[type] > 0)

  const fetchVersionAsync = async() => {
    const res = await api.getVersionAsync()
    const currentVersion = res.data && res.data.version

    const isVersionChanged = currentVersion !== version.value

    if (isVersionChanged || languages.value.length === 0) {
      await fetchLanguagesAsync()
      version.value = currentVersion
    }
    await setLanguageAsync(null, isVersionChanged)
  }

  const fetchCountrySubdivisionsAsync = async(countryId) => (countryId ? (await api.getAsync(`${api.countriesUrl}${countryId}/subdivisions/`)).data : [])

  const fetchLanguagesAsync = async() => {
    const res = await api.getLanguagesAsync()
    if (res.data) {
      languages.value = res.data
    }
  }

  const fetchLocalizationsAsync = async(languageCode = selectedLanguage.value, forceUpdate = false) => {
    if ((!localizations.value[languageCode]) || localizations.value[languageCode].length === 0 || forceUpdate) {
      const res = (await api.getLocalizationsAsync(languageCode))?.data
      if (res) {
        localizations.value[languageCode] = res
      }
    }
  }

  const getLocalizedLanguages = () => languages.value.map((item) => {
    const i18n = getI18n()
    return {
      ...item,
      name: i18n.t(item.name)
    }
  })
  const getLocalizedTags = () => getTranslatedItems(tags.value).sort((a, b) => a.name?.localeCompare(b.name))

  const findSupportedLanguage = (languageCode) => {
    if (!languageCode) return null
    const codes = languages.value.map(lang => lang.code)
    const [primarySubtag] = languageCode.split('-')
    return codes.find(code => code === languageCode) ??
      codes.find(code => code.split('-')[0] === primarySubtag) ??
      null
  }

  const getBrowserLanguage = () => (navigator.languages ?? [navigator.language])
    .map(findSupportedLanguage)
    .find(Boolean) ?? null

  const applyLanguageAsync = async(languageCode, forceUpdate) => {
    languageCode = findSupportedLanguage(languageCode || selectedLanguage.value) ??
      getBrowserLanguage() ??
      window.config.DEFAULT_LANGUAGE

    if (forceUpdate) {
      localizations.value = {}
    }

    await fetchLocalizationsAsync(languageCode)
    await updateLocaleAsync(languageCode, toRaw(localizations.value))
    await updateQuasarLocaleAsync(languageCode)
    selectedLanguage.value = languageCode
  }

  // Boot version check and auto-login set the language concurrently: serialize them.
  let languageQueue = Promise.resolve()

  const setLanguageAsync = (languageCode = null, forceUpdate = false) => {
    const run = languageQueue.then(() => applyLanguageAsync(languageCode, forceUpdate))
    languageQueue = run.catch(err => console.error('Failed to set language', err))
    return run
  }

  const fetchTagsAsync = async(params, store = false) => useFetchAndStoreAsync(tags, () => api.getTagsAsync(params), store)
  const getTagById = (id) => tags.value.find(tag => tag.id === id)

  const getLocalizedDurationDays = () => {
    const i18n = getI18n()
    return durationDaysOptions.map(({ value, labelKey }) => ({ value, label: i18n.t(labelKey) }))
  }

  const getLocalizedServiceAvailabilities = () => {
    const i18n = getI18n()
    const colorMap = {
      [serviceAvailabilityEnum.UNAVAILABLE]: 'red',
      [serviceAvailabilityEnum.PARTIALLY_AVAILABLE]: 'orange',
      [serviceAvailabilityEnum.AVAILABLE]: 'green',
    }
    return Object.keys(serviceAvailabilityEnum).map((i) => {
      const name = i18n.t(`common.serviceAvailabilities.${i.toLocaleLowerCase()}`)
      const color = colorMap[serviceAvailabilityEnum[i]]
      return { id: serviceAvailabilityEnum[i], name, label: name, color, textColor: textColor(color) }
    })
  }

  const getServiceAvailabilityPaletteColors = () => Object.fromEntries(
    getLocalizedServiceAvailabilities().map(a => [a.id, colors.getPaletteColor(a.color)]),
  )

  const getLocalizedServiceAvailabilityTypes = () => {
    const i18n = getI18n()
    return Object.keys(serviceAvailabilityTypeEnum).map((i) => {
      const name = i18n.t(`common.serviceAvailabilityTypes.${i.toLocaleLowerCase()}`)
      const color = serviceAvailabilityTypeEnum[i] === serviceAvailabilityTypeEnum.USUALLY_AVAILABLE ? 'green'
        : 'warning'

      return { id: serviceAvailabilityTypeEnum[i], name: name, label: name, color: color, textColor: textColor(color) }
    })
  }

  const getLocalizedServiceTypes = () => {
    const i18n = getI18n()
    const colorMap = {
      [serviceTypeEnum.STAFF]: 'blue',
      [serviceTypeEnum.EQUIPMENT]: 'teal',
      [serviceTypeEnum.GROUP]: 'deep-purple',
    }
    const iconMap = {
      [serviceTypeEnum.STAFF]: 'mdi-account-outline',
      [serviceTypeEnum.EQUIPMENT]: 'mdi-camcorder',
      [serviceTypeEnum.GROUP]: 'mdi-select-group',
    }
    return Object.keys(serviceTypeEnum).map((i) => {
      const name = i18n.t(`common.serviceTypes.${i.toLocaleLowerCase()}`)
      const color = colorMap[serviceTypeEnum[i]]
      return { id: serviceTypeEnum[i], name, label: name, color, textColor: textColor(color), icon: iconMap[serviceTypeEnum[i]] }
    })
  }

  const getLocalizedExtraFieldTypes = () => {
    const i18n = getI18n()
    return Object.keys(extraFieldTypeEnum).map((key) => {
      const id = extraFieldTypeEnum[key]
      return { id, name: i18n.t(`common.extraFieldTypes.${key.toLocaleLowerCase()}`), icon: extraFieldTypeIcons[id] }
    })
  }

  const getLocalizedServiceStructureTypes = () => {
    const i18n = getI18n()
    const colorMap = {
      [serviceStructureTypeEnum.PHYSICAL]: 'blue-grey',
      [serviceStructureTypeEnum.GROUP_PHYSICAL]: 'teal',
      [serviceStructureTypeEnum.GROUP_VIRTUAL]: 'deep-purple',
    }
    return Object.keys(serviceStructureTypeEnum).map((i) => {
      const color = colorMap[serviceStructureTypeEnum[i]]
      const name = i18n.t(`common.serviceStructureTypes.${i.toLocaleLowerCase()}`)
      return { id: serviceStructureTypeEnum[i], name, label: name, color, textColor: textColor(color) }
    })
  }

  const getLocalizedDrivingLicenseTypes = () => Object.keys(drivingLicenseTypeEnum).map((code) => ({ id: drivingLicenseTypeEnum[code], name: code, label: code }))

  const getLocalizedWeekdays = () => {
    const formatter = new Intl.DateTimeFormat(getI18n().locale.value, { weekday: 'long' })
    // 2024-01-01 is a Monday, so day N of that week has ISO weekday N.
    return Object.values(weekdayEnum).map(value => ({ value, label: formatter.format(new Date(2024, 0, value)) }))
  }

  const getLocalizedExchangeRateSources = () => {
    const i18n = getI18n()
    const colorMap = {
      [exchangeRateSourceEnum.MANUAL]: 'grey',
      [exchangeRateSourceEnum.ECB]: 'blue',
      [exchangeRateSourceEnum.ECB_DERIVED]: 'light-blue',
    }
    return Object.keys(exchangeRateSourceEnum).map((i) => {
      const name = i18n.t(`common.exchangeRateSources.${i.toLocaleLowerCase()}`)
      const color = colorMap[exchangeRateSourceEnum[i]]
      return { id: exchangeRateSourceEnum[i], name, label: name, color, textColor: textColor(color) }
    })
  }

  const getLocalizedAccessLevels = () => {
    const i18n = getI18n()
    const meta = {
      [accessLevelEnum.NONE]: { key: 'none', color: 'grey-5', icon: null },
      [accessLevelEnum.VIEW]: { key: 'view', color: 'blue-grey', icon: 'mdi-eye-outline' },
      [accessLevelEnum.FULL]: { key: 'full', color: 'primary', icon: 'mdi-check' },
    }
    return Object.keys(meta).map((value) => {
      const { key, color, icon } = meta[value]
      const name = i18n.t(`common.accessLevels.${key}`)
      const hint = key === 'none' ? null : i18n.t(`common.accessLevelHints.${key}`)
      return { id: Number(value), name, label: name, hint, color, icon, textColor: textColor(color) }
    })
  }

  const getLocalizedPortalAccessStatuses = () => {
    const i18n = getI18n()
    const meta = {
      [portalAccessStatusEnum.PENDING]: { key: 'common.portalAccessStatuses.pending', color: 'orange' },
      [portalAccessStatusEnum.ACTIVATED]: { key: 'common.portalAccessStatuses.activated', color: 'green' },
      [portalAccessStatusEnum.EXPIRED]: { key: 'common.portalAccessStatuses.expired', color: 'red' },
    }
    return Object.keys(meta).map((value) => {
      const name = i18n.t(meta[value].key)
      const color = meta[value].color
      return { id: Number(value), name: name, label: name, color: color, textColor: textColor(color) }
    })
  }

  const getLocalizedReferenceTypes = () => {
    const i18n = getI18n()
    const meta = {
      0: { key: 'common.phone', icon: 'mdi-phone', color: 'blue' },
      1: { key: 'common.email', icon: 'mdi-email', color: 'orange' },
      2: { key: 'common.social', icon: 'mdi-web', color: 'deep-purple' },
    }
    return Object.keys(meta).map((i) => {
      const name = i18n.t(meta[i].key)
      return { id: Number(i), name, label: name, icon: meta[i].icon, color: meta[i].color, textColor: textColor(meta[i].color) }
    })
  }

  const getLocalizedConversationTypes = () => {
    const i18n = getI18n()
    const meta = {
      0: { key: 'common.phone', icon: 'mdi-phone', color: 'green' },
      1: { key: 'common.email', icon: 'mdi-email', color: 'blue' },
      2: { key: 'common.textMessage', icon: 'mdi-message-text', color: 'teal' },
      3: { key: 'common.inPerson', icon: 'mdi-account-group', color: 'orange' },
    }
    return Object.keys(meta).map((i) => {
      const name = i18n.t(meta[i].key)
      return { id: Number(i), name, label: name, icon: meta[i].icon, color: meta[i].color, textColor: textColor(meta[i].color) }
    })
  }

  const localizedNotificationTypes = computed(() => getLocalizedEnumItems(notificationTypeEnum, 'common.notificationTypes'))

  const localizedNotificationCategories = computed(() => getLocalizedEnumItems(notificationCategoryEnum, 'common.notificationCategories').map((category) => {
    const color = colors.getPaletteColor(notificationCategoryPaletteEnum[category.id])
    return { ...category, color, textColor: textColor(color) }
  }))

  const localizedNotificationFrequencies = computed(() => getLocalizedEnumItems(notificationFrequencyEnum, 'common.notificationFrequencies'))

  const localizedNotificationReminders = computed(() => getLocalizedEnumItems(notificationReminderEnum, 'common.notificationReminders'))

  const getLocalizedNotificationTypes = () => localizedNotificationTypes.value
  const getLocalizedNotificationCategories = () => localizedNotificationCategories.value
  const getLocalizedNotificationFrequencies = () => localizedNotificationFrequencies.value
  const getLocalizedNotificationReminders = () => localizedNotificationReminders.value

  const increaseFontSize = () => {
    fontSize.value += 0.05
    document.documentElement.style.fontSize = fontSize.value + 'rem'
  }
  const decreaseFontSize = () => {
    fontSize.value -= 0.05
    document.documentElement.style.fontSize = fontSize.value + 'rem'
  }

  const $reset = () => {
    fontSize.value = 1
    languages.value = []
    localizations.value = {}
    tags.value = []
    selectedLanguage.value = window.config.DEFAULT_LANGUAGE
    version.value = null
  }

  const localizationsReset = () => {
    localizations.value = {}
    languages.value = []
  }

  return {
    api,
    fetchCountrySubdivisionsAsync,
    weekdayEnum,
    getLocalizedWeekdays,
    accessLevelEnum,
    permissionFlagEnum,
    systemRoleEnum,
    dateRangePresetEnum,
    durationDaysOptions,
    extraFieldTypeEnum,
    fontSize,
    languages,
    localizations,
    notificationTypeEnum,
    notificationCategoryEnum,
    notificationScopeEnum,
    notificationTargetEnum,
    notificationFrequencyEnum,
    notificationReminderEnum,
    portalAccessStatusEnum,
    reloadKey,
    lastEntityChange,
    entityChangeTypeEnum,
    selectedLanguage,
    serviceAvailabilityEnum,
    serviceAvailabilityTypeEnum,
    serviceStructureTypeEnum,
    drivingLicenseTypeEnum,
    serviceTypeEnum,
    tags,
    version,
    $reset,
    decreaseFontSize,
    fetchLanguagesAsync,
    fetchLocalizationsAsync,
    fetchTagsAsync,
    fetchVersionAsync,
    getBrowserLanguage,
    getLocalizedDurationDays,
    getLocalizedNotificationTypes,
    getLocalizedNotificationCategories,
    getLocalizedNotificationFrequencies,
    getLocalizedNotificationReminders,
    getLocalizedLanguages,
    getLocalizedServiceAvailabilities,
    getServiceAvailabilityPaletteColors,
    getLocalizedServiceAvailabilityTypes,
    getLocalizedServiceTypes,
    getLocalizedServiceStructureTypes,
    getLocalizedExtraFieldTypes,
    getLocalizedDrivingLicenseTypes,
    getLocalizedTags,
    getLocalizedExchangeRateSources,
    getLocalizedAccessLevels,
    getLocalizedPortalAccessStatuses,
    getLocalizedReferenceTypes,
    getLocalizedConversationTypes,
    getTagById,
    increaseFontSize,
    incrementReloadKey,
    notifyEntityChange,
    subscribeEntityChanges,
    hasEntityChangeSubscribers,
    localizationsReset,
    setLanguageAsync,
  }
}, {
  persist: {
    storage: localStorage,
    paths: [
      'fontSize',
      'languages',
      'localizations',
      'selectedLanguage',
      'tags',
      'version',
    ],
  },
})
